/**
 * StatusCell - the lead's status ON THIS STUDY, changed in the row
 * (docs/plans/crm-recruitment-list-plan.md Part A sections 3 step 4 and 6, B4).
 *
 * The lead page's own control: LifecycleSelect over the membership vocabulary, with its per-study
 * `commit` pointed at PATCH /api/crm/contacts/:id/studies/:studyId, exactly the Studies card's
 * wiring (components/StudiesCard.tsx). So the reason modal for Not interested / Unreachable /
 * Screen failed / Not eligible (with the optional failed-question picker), the randomized-elsewhere
 * acknowledgement and the optimistic revert all come with it, and this build adds no status write
 * path of its own. A change that landed updates the row through onRowChange; a row moved to a
 * closed status stays where it is, greyed, until the next manual refresh (decision 6).
 */
import { useCallback, useEffect, useRef } from 'react';
import api from '../../../../services/api';
import LifecycleSelect from '../../../components/LifecycleSelect';
import { MEMBERSHIP_STATUSES } from '../../../lib';
import type { RecruitRow, RecruitRowChangeHandler, RecruitStudy } from '../../types';

export interface StatusCellProps {
  row: RecruitRow;
  /** The study the list is for: the per-study PATCH's :studyId. */
  study: Pick<RecruitStudy, 'id'>;
  onRowChange: RecruitRowChangeHandler;
  /** Sheet keyboard: Enter opened this cell (focus the select and open its list). */
  editing?: boolean;
  /** The edit finished (a change landed). */
  onEditEnd?: () => void;
  className?: string;
}

export function StatusCell({ row, study, onRowChange, editing = false, onEditEnd, className = '' }: StatusCellProps) {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const contactId = row.contact.id;
  const studyId = study.id;
  const onEditEndRef = useRef(onEditEnd);
  onEditEndRef.current = onEditEnd;

  // Enter on the cell: focus the select and, where the browser allows it from a key press, open
  // its list (showPicker; Chrome 121+). Anywhere else, focus alone lets the arrows pick.
  useEffect(() => {
    if (!editing) return;
    const select = wrapRef.current?.querySelector('select');
    if (!select) return;
    select.focus();
    const picker = (select as HTMLSelectElement & { showPicker?: () => void }).showPicker;
    if (typeof picker === 'function') {
      try {
        picker.call(select);
      } catch {
        /* not allowed without a fresh user gesture: the focused select still works */
      }
    }
  }, [editing]);

  const commit = useCallback(async (next: string, extra: Record<string, string | boolean | string[]>) => {
    await api.patch(`/api/crm/contacts/${contactId}/studies/${studyId}`, { status: next, ...extra });
  }, [contactId, studyId]);

  const onChanged = useCallback((next: string) => {
    const at = new Date().toISOString();
    onRowChange(contactId, (r) => ({
      ...r,
      membership: { ...r.membership, status: next, status_changed_at: at },
    }));
    onEditEndRef.current?.();
  }, [contactId, onRowChange]);

  return (
    <span ref={wrapRef} className={`block min-w-0 max-w-full ${className}`}>
      <LifecycleSelect
        contactId={contactId}
        value={row.membership.status}
        statuses={MEMBERSHIP_STATUSES}
        primaryStudyId={studyId}
        commit={commit}
        onChanged={onChanged}
        wrapperClassName="flex w-full min-w-0"
        className="w-full min-w-0 truncate"
      />
    </span>
  );
}

export default StatusCell;
